import React, { useState, useRef, useImperativeHandle } from 'react';
import styled from 'astroturf/react';
import useTranslationTest from 'js/utils/hooks/useTranslationTest';
import {
  KiiTypeahead,
  SearchType,
  InputOption,
  KiiTypeaheadHandle,
} from 'js/kii_component/KiiTypeahead';
import {
  ContentContainer,
  Prompt,
} from '../reg_path/signup_to_download_corex/shared/utils';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { KiiSource } from './api/KiiDataApi';
import { KiiMatchingComponentHandle } from './KiiComponent';

const TRANSLATIONS_NAMESPACE = 'RegPath.SignupToDownload.PaperTitle';

const InputWrapper = styled.div`
  // Cancel default input styles
  margin: -8px 0;
`;

const AfterInputWrapper = styled.div`
  // HACK: This, combined with the relative positioning of the skip button, is a
  // hacky way to move elements closer to the typeahead input. The input sets
  // itself very large to make sure it has room for the typeahead overlay, but
  // that prevents subsequent elements from appearing next to it. To account for
  // that, we force elements upwards in the page so they can be closer.

  // This is around the size of the footer, which lets the typeahead overlay
  // overlap the footer a bit.
  margin-top: -105px;
`;

const Skip = styled.p`
  @import 'stylesheets/design_system/font.scss';

  // This pushes this button up in the page so that it will sit closer to the
  // typeahead. Note that the typeahead expands when something is selected, so
  // we don't want to get it too close by default.
  position: relative;
  top: -150px;
  margin: 0;

  @include body__large;
  text-decoration-line: underline;
  cursor: pointer;
`;

const UnfilledWarning = styled.p`
  @import 'stylesheets/design_system/font.scss';

  @include body__large;

  color: var(--text-error, #b60000);
  margin-top: 0;
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

type Props = {
  kiiSource: KiiSource;
  label?: string;
  onSkip?: () => void;
  onSuccessfulMatch?:
    | ((taggingId: number) => void)
    | ((taggingId: number) => Promise<void>);
  onFailedMatch?: () => void;
};

export const PaperTitle = React.forwardRef<KiiMatchingComponentHandle, Props>(
  (
    { kiiSource, label, onSkip, onSuccessfulMatch, onFailedMatch }: Props,
    ref,
  ) => {
    const [t] = useTranslationTest(TRANSLATIONS_NAMESPACE, true);

    const kiiRef = useRef<KiiTypeaheadHandle>(null);

    const [typedText, setTypedText] = useState<string>('');
    const [showUnfilledWarning, setShowUnfilledWarning] =
      useState<boolean>(false);

    const handleMissingSubmit = () => {
      onSkip && onSkip();
    };

    const handleInputChange = (option: InputOption) => {
      setTypedText(option.option.label);
    };

    const submit = async () => {
      if (!kiiRef.current) {
        return;
      } else if (typedText !== '') {
        // Submit the KII modal and check the response to see if any of the inputs
        // produced valid author matches
        const kiiResponse = await kiiRef.current.submit('sutd', true, false);
        if (
          !kiiResponse.error &&
          kiiResponse.result?.tagging_matches?.some(Boolean)
        ) {
          // Find the id of the tag which had a valid match
          const matchedTagIndex =
            kiiResponse.result.tagging_matches.findIndex(Boolean);
          const tagId = kiiResponse.result.tagging_ids[matchedTagIndex];

          onSuccessfulMatch && (await onSuccessfulMatch(tagId));
        } else {
          onFailedMatch && onFailedMatch();
        }
      } else {
        setShowUnfilledWarning(true);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: submit,
    }));

    return (
      <ContentContainer>
        {showUnfilledWarning && (
          <UnfilledWarning>
            <Icon name="warning" size={25} />
            {t('skipwarning')}
          </UnfilledWarning>
        )}
        {label && (
          <Prompt size="lg" bold>
            {label}
          </Prompt>
        )}
        <InputWrapper>
          <ImpressionWatcher source={`${kiiSource}_paper_typeahead`}>
            <KiiTypeahead
              ref={kiiRef}
              placeholderText={t('placeholder')}
              emptyErrorText="None"
              searchType={SearchType.PaperTitleAll}
              onChange={handleInputChange}
              height={400}
            />
          </ImpressionWatcher>
        </InputWrapper>
        <AfterInputWrapper>
          <ImpressionWatcher source={`${kiiSource}_paper_typeahead_skip`}>
            <Skip role="button" onClick={handleMissingSubmit}>
              {t('skip')}
            </Skip>
          </ImpressionWatcher>
        </AfterInputWrapper>
      </ContentContainer>
    );
  },
);
